import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import {
  bindingCreateSchema,
  bindingSchema,
  resourceCreateSchema,
  resourceDetailSchema,
  resourcePageSchema,
  resourceSchema,
  resourceUpdateSchema,
  type BindingCreate,
  type Resource,
  type ResourceCreate,
  type ResourceUpdate,
} from '@bekebi/contracts/api';
import { apiRequest } from '../../api/client';
import { serviceKeys } from '../services/api';

/**
 * Data access for the Admin Infrastructure area — the existing endpoints only:
 *   GET    /api/infrastructure/resources[?cursor=&limit=]       (infrastructure.view)
 *   POST   /api/infrastructure/resources                         (infrastructure.manage)
 *   GET    /api/infrastructure/resources/:id  (children, linked services)  (infrastructure.view)
 *   PATCH  /api/infrastructure/resources/:id  {name?, externalRef?, parentResourceId?} (manage)
 *   POST   /api/services/:serviceId/bindings  {resourceId, role, scopeRef?}   (manage)
 *   DELETE /api/services/:serviceId/bindings/:bindingId   removes the link only  (manage)
 * The inventory holds names, kinds and references — never credentials — and no health.
 */
export const infraKeys = {
  all: ['infrastructure'] as const,
  list: () => [...infraKeys.all, 'list'] as const,
  detail: (id: string) => [...infraKeys.all, 'detail', id] as const,
};

export function useResources() {
  return useInfiniteQuery({
    queryKey: infraKeys.list(),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ limit: '50' });
      if (pageParam) params.set('cursor', pageParam);
      return apiRequest(`/infrastructure/resources?${params}`, { schema: resourcePageSchema });
    },
    getNextPageParam: (last) => last.nextCursor,
  });
}

/** Every resource (bounded), for choosing a parent. */
export function useAllResources(enabled: boolean) {
  return useQuery({
    queryKey: [...infraKeys.all, 'all'],
    queryFn: async (): Promise<Resource[]> => {
      const items: Resource[] = [];
      let cursor: string | null = null;
      for (let page = 0; page < 20; page += 1) {
        const params = new URLSearchParams({ limit: '100' });
        if (cursor) params.set('cursor', cursor);
        const result = await apiRequest(`/infrastructure/resources?${params}`, {
          schema: resourcePageSchema,
        });
        items.push(...result.items);
        cursor = result.nextCursor;
        if (!cursor) break;
      }
      return items;
    },
    enabled,
  });
}

export function useResource(id: string) {
  return useQuery({
    queryKey: infraKeys.detail(id),
    queryFn: () => apiRequest(`/infrastructure/resources/${id}`, { schema: resourceDetailSchema }),
  });
}

/** Bindings change resources and services alike: re-read both. */
async function refresh(queryClient: QueryClient) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: infraKeys.all }),
    queryClient.invalidateQueries({ queryKey: serviceKeys.all }),
  ]);
}

export function useCreateResource() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ResourceCreate) =>
      apiRequest('/infrastructure/resources', {
        method: 'POST',
        body: resourceCreateSchema.parse(input),
        schema: resourceSchema,
      }),
    onSuccess: async () => refresh(queryClient),
  });
}

export function useUpdateResource(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: ResourceUpdate) =>
      apiRequest(`/infrastructure/resources/${id}`, {
        method: 'PATCH',
        body: resourceUpdateSchema.parse(patch),
        schema: resourceSchema,
      }),
    onSuccess: async () => refresh(queryClient),
  });
}

export function useCreateBinding() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ serviceId, ...body }: BindingCreate & { serviceId: string }) =>
      apiRequest(`/services/${serviceId}/bindings`, {
        method: 'POST',
        body: bindingCreateSchema.parse(body),
        schema: bindingSchema,
      }),
    onSuccess: async () => refresh(queryClient),
  });
}

export function useRemoveBinding() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ serviceId, bindingId }: { serviceId: string; bindingId: string }) =>
      apiRequest(`/services/${serviceId}/bindings/${bindingId}`, { method: 'DELETE' }),
    // Not awaited: the refreshed data drops this binding's row, which would unmount the caller
    // before its success callback (the "binding was removed" message) could run.
    onSuccess: () => {
      void refresh(queryClient);
    },
  });
}
